import type { QueryParams } from '@/lib/types/common.types';
import type {
  Attachment,
  Conversation,
  CreateConversationInput,
  CreateMessageInput,
  Message,
} from '@/lib/types/message.types';
import apiClient from './client';
import { extractArrayResponse, extractResponseData, handleApiError } from './responseHelpers';

// Transform backend attachments (string[]) to frontend Attachment[]
function transformAttachments(attachments: string[] | any[]): Attachment[] {
  if (!Array.isArray(attachments)) {
    return [];
  }

  return attachments.map((att, index) => {
    // If it's already an Attachment object, return it
    if (typeof att === 'object' && att !== null && 'url' in att) {
      return {
        id: att.id || `att-${index}`,
        name: att.name || att.url?.split('/').pop()?.split('?')[0] || 'attachment',
        url: att.url || att.filePath || '',
        filePath: att.filePath,
        type: att.type || 'file',
        size: att.size || 0,
        uploadedAt: att.uploadedAt,
      };
    }

    // If it's a string (URL), convert to Attachment object
    if (typeof att === 'string') {
      const url = att;
      const fileName = url.split('/').pop()?.split('?')[0] || 'attachment';
      const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName) || url.includes('image') || url.includes('cloudinary.com');
      
      return {
        id: `att-${index}`,
        name: fileName,
        url: url,
        filePath: url,
        type: isImage ? 'image' : 'file',
        size: 0,
      };
    }

    // Fallback
    return {
      id: `att-${index}`,
      name: 'attachment',
      url: '',
      type: 'file',
      size: 0,
    };
  });
}

// Transform backend conversation to frontend Conversation
function transformConversation(backendConversation: any): Conversation {
  return {
    id: backendConversation.id || '',
    requestId: backendConversation.request_id || backendConversation.requestId || null,
    requestTitle: backendConversation.request_title || backendConversation.requestTitle || null,
    requestName: backendConversation.request_name || backendConversation.requestName || null,
    offerId: backendConversation.offer_id || backendConversation.offerId || null,
    projectId: backendConversation.project_id || backendConversation.projectId || null,
    contractId: backendConversation.contract_id || backendConversation.contractId || null,
    participants: (backendConversation.participants || []).map((p: any) => String(p)),
    participantsInfo: backendConversation.participants_info || backendConversation.participantsInfo
      ? (backendConversation.participants_info || backendConversation.participantsInfo).map((p: any) => ({
          id: String(p.id || ''),
          fullName: p.full_name || p.fullName || '',
          avatar: p.avatar || '',
          userType: p.user_type || p.userType,
        }))
      : undefined,
    lastMessageId: backendConversation.last_message_id || backendConversation.lastMessageId || null,
    lastMessage: backendConversation.last_message || backendConversation.lastMessage
      ? transformMessage(backendConversation.last_message || backendConversation.lastMessage)
      : undefined,
    lastMessageAt: backendConversation.last_message_at || backendConversation.lastMessageAt || '',
    status: (backendConversation.status || 'active') as Conversation['status'],
    unreadCount: backendConversation.unread_count || backendConversation.unreadCount || 0,
    createdAt: backendConversation.created_at || backendConversation.createdAt || '',
    updatedAt: backendConversation.updated_at || backendConversation.updatedAt || '',
  };
}

// Transform backend message to frontend Message
function transformMessage(backendMessage: any): Message {
  const message: Message = {
    id: backendMessage.id || backendMessage.ID || '',
    conversationId: backendMessage.conversationId || backendMessage.conversation_id || '',
    senderId: backendMessage.senderId || backendMessage.sender_id || '',
    sender: backendMessage.sender
      ? {
          id: backendMessage.sender.id || backendMessage.sender.ID || '',
          fullName: backendMessage.sender.fullName || backendMessage.sender.full_name || '',
          avatar: backendMessage.sender.avatar || '',
          userType: backendMessage.sender.userType || backendMessage.sender.user_type,
        }
      : undefined,
    content: backendMessage.content || '',
    messageType: backendMessage.messageType || backendMessage.message_type || 'text',
    attachments: transformAttachments(backendMessage.attachments || []),
    isRead: backendMessage.isRead ?? backendMessage.is_read ?? false,
    readAt: backendMessage.readAt || backendMessage.read_at || null,
    isSystemMessage: backendMessage.isSystemMessage ?? backendMessage.is_system_message ?? false,
    createdAt: backendMessage.createdAt || backendMessage.created_at || '',
    updatedAt: backendMessage.updatedAt || backendMessage.updated_at || '',
  };

  return message;
}

export const messagesApi = {
  // Conversations
  getAllConversations: async (params?: QueryParams): Promise<Conversation[]> => {
    try {
      const response = await apiClient.get<{ data: any[]; pagination?: any }>(
        '/v1/conversations',
        { params }
      );
      const conversations = extractArrayResponse<any>(response);
      return conversations.map(transformConversation);
    } catch (error) {
      handleApiError(error);
    }
  },

  getConversation: async (id: string): Promise<Conversation> => {
    try {
      const response = await apiClient.get<{ data: any } | any>(
        `/v1/conversations/${id}`
      );
      const backendConversation = extractResponseData<any>(response);
      return transformConversation(backendConversation);
    } catch (error) {
      handleApiError(error);
    }
  },

  createConversation: async (conversationData: CreateConversationInput): Promise<Conversation> => {
    try {
      const response = await apiClient.post<
        { message?: string; data: any } | any
      >('/v1/conversations', conversationData);
      const backendConversation = extractResponseData<any>(response);
      return transformConversation(backendConversation);
    } catch (error) {
      handleApiError(error);
    }
  },

  closeConversation: async (id: string): Promise<Conversation> => {
    try {
      const response = await apiClient.patch<
        { message?: string; data: any } | any
      >(`/v1/conversations/${id}/close`);
      const backendConversation = extractResponseData<any>(response);
      return transformConversation(backendConversation);
    } catch (error) {
      handleApiError(error);
    }
  },

  archiveConversation: async (id: string): Promise<Conversation> => {
    try {
      const response = await apiClient.patch<
        { message?: string; data: any } | any
      >(`/v1/conversations/${id}/archive`);
      const backendConversation = extractResponseData<any>(response);
      return transformConversation(backendConversation);
    } catch (error) {
      handleApiError(error);
    }
  },

  unarchiveConversation: async (id: string): Promise<Conversation> => {
    try {
      const response = await apiClient.patch<
        { message?: string; data: any } | any
      >(`/v1/conversations/${id}/unarchive`);
      const backendConversation = extractResponseData<any>(response);
      return transformConversation(backendConversation);
    } catch (error) {
      handleApiError(error);
    }
  },

  // ⚠️ NOTE: Mute/Unmute endpoints are not implemented in Backend yet
  // These functions are kept for future implementation
  // TODO: Implement mute/unmute endpoints in Backend or remove these functions
  muteConversation: async (id: string): Promise<Conversation> => {
    throw new Error('MESSAGES_MUTE_NOT_AVAILABLE');
    // const response = await apiClient.patch<{ message?: string; data: Conversation } | Conversation>(
    //   `/v1/conversations/${id}/mute`
    // );
    // if (response.data && typeof response.data === 'object' && 'data' in response.data) {
    //   return (response.data as { data: Conversation }).data;
    // } else if (response.data && !('data' in response.data)) {
    //   return response.data as unknown as Conversation;
    // } else {
    //   throw new Error('Invalid response format');
    // }
  },

  unmuteConversation: async (id: string): Promise<Conversation> => {
    throw new Error('MESSAGES_UNMUTE_NOT_AVAILABLE');
    // const response = await apiClient.patch<{ message?: string; data: Conversation } | Conversation>(
    //   `/v1/conversations/${id}/unmute`
    // );
    // if (response.data && typeof response.data === 'object' && 'data' in response.data) {
    //   return (response.data as { data: Conversation }).data;
    // } else if (response.data && !('data' in response.data)) {
    //   return response.data as unknown as Conversation;
    // } else {
    //   throw new Error('Invalid response format');
    // }
  },

  // Messages
  getMessages: async (conversationId: string, params?: QueryParams): Promise<Message[]> => {
    try {
      const response = await apiClient.get<{ data: any[]; pagination?: any }>(
        `/v1/conversations/${conversationId}/messages`,
        { params }
      );
      const messages = extractArrayResponse<any>(response);
      return messages.map(transformMessage);
    } catch (error) {
      handleApiError(error);
    }
  },

  sendMessage: async (
    conversationId: string,
    messageData: CreateMessageInput,
    attachments?: File[]
  ): Promise<Message> => {
    try {
      const hasAttachmentUrls = Boolean(messageData.attachmentUrls && messageData.attachmentUrls.length > 0);
      const normalizedContent =
        messageData.content && messageData.content.trim().length > 0
          ? messageData.content
          : hasAttachmentUrls
            ? '\u200B' // Backend requires non-empty content; keep UI blank
            : messageData.content;

      // If we have Cloudinary URLs, send as JSON
      if (hasAttachmentUrls) {
        // Backend expects attachments as []string (array of URLs)
        const response = await apiClient.post<{ message?: string; data: any } | any>(
          `/v1/conversations/${conversationId}/messages`,
          {
            content: normalizedContent,
            messageType: messageData.messageType || 'text',
            attachments: messageData.attachmentUrls, // Send URLs directly as strings
          }
        );
        const backendMessage = extractResponseData<any>(response);
        return transformMessage(backendMessage);
      }

      // Otherwise, use FormData for file uploads
      const formData = new FormData();
      formData.append('content', normalizedContent || '');
      if (messageData.messageType) {
        formData.append('messageType', messageData.messageType);
      }
      if (attachments && attachments.length > 0) {
        attachments.forEach((file) => formData.append('attachments', file));
      }
      const response = await apiClient.post<{ message?: string; data: any } | any>(
        `/v1/conversations/${conversationId}/messages`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const backendMessage = extractResponseData<any>(response);
      return transformMessage(backendMessage);
    } catch (error) {
      handleApiError(error);
    }
  },

  updateMessage: async (id: string, content: string): Promise<Message> => {
    try {
      const response = await apiClient.put<{ message?: string; data: any } | any>(
        `/v1/messages/${id}`,
        { content }
      );
      const backendMessage = extractResponseData<any>(response);
      return transformMessage(backendMessage);
    } catch (error) {
      handleApiError(error);
    }
  },

  deleteMessage: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/messages/${id}`);
  },

  markMessageAsRead: async (id: string): Promise<Message> => {
    try {
      const response = await apiClient.patch<{ message?: string; data: any } | any>(
        `/v1/messages/${id}/read`
      );
      const backendMessage = extractResponseData<any>(response);
      return transformMessage(backendMessage);
    } catch (error) {
      handleApiError(error);
    }
  },

  markAllAsRead: async (conversationId: string): Promise<void> => {
    await apiClient.patch(`/v1/conversations/${conversationId}/messages/read-all`);
  },

  // Attachments
  addAttachment: async (messageId: string, file: File): Promise<{ id: string; url: string }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<
        { message?: string; data: { id: string; url: string } } | { id: string; url: string }
      >(`/v1/messages/${messageId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return extractResponseData<{ id: string; url: string }>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  deleteAttachment: async (messageId: string, attachmentId: string): Promise<void> => {
    await apiClient.delete(`/v1/messages/${messageId}/attachments/${attachmentId}`);
  },

  // System Messages
  sendSystemMessage: async (conversationId: string, content: string): Promise<Message> => {
    try {
      const response = await apiClient.post<{ message?: string; data: any } | any>(
        `/v1/conversations/${conversationId}/system-message`,
        { content }
      );
      const backendMessage = extractResponseData<any>(response);
      return transformMessage(backendMessage);
    } catch (error) {
      handleApiError(error);
    }
  },

  // Query
  getUnreadCount: async (): Promise<{ count: number }> => {
    try {
      const response = await apiClient.get<{ data: { count: number } } | { count: number }>(
        '/v1/messages/unread/count'
      );
      return extractResponseData<{ count: number }>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  searchMessages: async (query: string, params?: QueryParams): Promise<Message[]> => {
    try {
      const response = await apiClient.get<{ data: Message[]; pagination?: any }>(
        '/v1/messages/search',
        {
          params: { q: query, ...params },
        }
      );
      return extractArrayResponse<Message>(response);
    } catch (error) {
      handleApiError(error);
    }
  },
};
